import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type {
  CustomizationSource,
  OrderingErrorMessagesState,
} from 'slices/orderingErrorMessages/types'
import { DEFAULT_BRAND_TONE } from 'slices/orderingErrorMessages/types'

const initialState: OrderingErrorMessagesState = {
  customizations: {},
  initialCustomizations: {},
  customizationSources: {},
  initialCustomizationSources: {},
  lastUsedBrandTone: '',
  aiModal: {
    isOpen: false,
    brandTone: '',
  },
  pendingAiSuggestions: {},
  preAiCustomizations: null,
  generatingCategoryIds: [],
  pendingSingleCategoryId: null,
}

const orderingErrorMessagesSlice = createSlice({
  name: 'orderingErrorMessages',
  initialState,
  reducers: {
    initializeCustomizations: (
      state,
      action: PayloadAction<{
        categories: Array<{
          id: string
          custom_message: string | null
          source?: 'ai' | 'manual' | null
        }>
        brandTone?: string | null
      }>
    ) => {
      const customizations: Record<string, string | null> = {}
      const sources: Record<string, CustomizationSource> = {}
      action.payload.categories.forEach(cat => {
        customizations[cat.id] = cat.custom_message
        if (cat.custom_message !== null && cat.custom_message !== '') {
          sources[cat.id] = cat.source === 'ai' ? 'ai' : 'manual'
        }
      })
      state.customizations = customizations
      state.initialCustomizations = { ...customizations }
      state.customizationSources = sources
      state.initialCustomizationSources = { ...sources }
      if (action.payload.brandTone) {
        state.lastUsedBrandTone = action.payload.brandTone
      }
    },
    setCustomization: (
      state,
      action: PayloadAction<{ categoryId: string; message: string | null }>
    ) => {
      const { categoryId, message } = action.payload
      state.customizations[categoryId] = message
      if (message !== null && message !== '') {
        state.customizationSources[categoryId] = 'manual'
      } else {
        delete state.customizationSources[categoryId]
      }
    },
    setCustomizations: (
      state,
      action: PayloadAction<Record<string, string | null>>
    ) => {
      state.customizations = action.payload
    },
    applyAiSuggestions: (
      state,
      action: PayloadAction<
        Array<{ category_id: string; custom_message: string }>
      >
    ) => {
      action.payload.forEach(suggestion => {
        state.customizations[suggestion.category_id] = suggestion.custom_message
        state.customizationSources[suggestion.category_id] = 'ai'
      })
    },
    publishSuccess: state => {
      state.initialCustomizations = { ...state.customizations }
      state.initialCustomizationSources = { ...state.customizationSources }
      state.preAiCustomizations = null
    },
    openAiModal: state => {
      state.aiModal.isOpen = true
      state.aiModal.brandTone = state.lastUsedBrandTone || DEFAULT_BRAND_TONE
    },
    closeAiModal: state => {
      state.aiModal.isOpen = false
      state.aiModal.brandTone = ''
    },
    setBrandTone: (state, action: PayloadAction<string>) => {
      state.aiModal.brandTone = action.payload
    },
    setLastUsedBrandTone: (state, action: PayloadAction<string>) => {
      state.lastUsedBrandTone = action.payload
    },
    snapshotForAi: state => {
      state.preAiCustomizations = { ...state.customizations }
    },
    revertCustomization: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload
      if (state.preAiCustomizations !== null) {
        state.customizations[categoryId] =
          state.preAiCustomizations[categoryId] ?? null
      } else {
        state.customizations[categoryId] =
          state.initialCustomizations[categoryId] ?? null
      }
      // Revert source too
      const initialSource = state.initialCustomizationSources[categoryId]
      if (initialSource) {
        state.customizationSources[categoryId] = initialSource
      } else {
        delete state.customizationSources[categoryId]
      }
    },
    setPendingAiSuggestions: (
      state,
      action: PayloadAction<
        Array<{ category_id: string; custom_message: string }>
      >
    ) => {
      const pending: Record<string, string> = {}
      action.payload.forEach(s => {
        pending[s.category_id] = s.custom_message
      })
      state.pendingAiSuggestions = pending
    },
    acceptAiSuggestion: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload
      const suggestion = state.pendingAiSuggestions[categoryId]
      if (suggestion !== undefined) {
        state.customizations[categoryId] = suggestion
        state.customizationSources[categoryId] = 'ai'
        delete state.pendingAiSuggestions[categoryId]
      }
    },
    rejectAiSuggestion: (state, action: PayloadAction<string>) => {
      delete state.pendingAiSuggestions[action.payload]
    },
    acceptAllAiSuggestions: state => {
      Object.entries(state.pendingAiSuggestions).forEach(
        ([categoryId, message]) => {
          state.customizations[categoryId] = message
          state.customizationSources[categoryId] = 'ai'
        }
      )
      state.pendingAiSuggestions = {}
    },
    rejectAllAiSuggestions: state => {
      state.pendingAiSuggestions = {}
    },
    addGeneratingCategoryId: (state, action: PayloadAction<string>) => {
      if (!state.generatingCategoryIds.includes(action.payload)) {
        state.generatingCategoryIds.push(action.payload)
      }
    },
    removeGeneratingCategoryId: (state, action: PayloadAction<string>) => {
      state.generatingCategoryIds = state.generatingCategoryIds.filter(
        id => id !== action.payload
      )
    },
    setPendingSingleCategoryId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.pendingSingleCategoryId = action.payload
    },
  },
})

export const {
  acceptAiSuggestion,
  acceptAllAiSuggestions,
  applyAiSuggestions,
  closeAiModal,
  initializeCustomizations,
  openAiModal,
  publishSuccess,
  rejectAiSuggestion,
  rejectAllAiSuggestions,
  revertCustomization,
  setBrandTone,
  setCustomization,
  setCustomizations,
  addGeneratingCategoryId,
  removeGeneratingCategoryId,
  setLastUsedBrandTone,
  setPendingAiSuggestions,
  setPendingSingleCategoryId,
  snapshotForAi,
} = orderingErrorMessagesSlice.actions

export const reducer = orderingErrorMessagesSlice.reducer

export const selectOrderingErrorMessages = (state: {
  orderingErrorMessages: OrderingErrorMessagesState
}) => state.orderingErrorMessages

export const selectHasChanges = (state: {
  orderingErrorMessages: OrderingErrorMessagesState
}) => {
  const { customizations, initialCustomizations } = state.orderingErrorMessages
  return Object.keys(customizations).some(
    key => customizations[key] !== initialCustomizations[key]
  )
}

export const selectCustomization =
  (categoryId: string) =>
  (state: { orderingErrorMessages: OrderingErrorMessagesState }) =>
    state.orderingErrorMessages.customizations[categoryId] ?? null

export const selectCustomizationSource =
  (categoryId: string) =>
  (state: { orderingErrorMessages: OrderingErrorMessagesState }) =>
    state.orderingErrorMessages.customizationSources[categoryId] ?? null

export const selectInitialCustomization =
  (categoryId: string) =>
  (state: { orderingErrorMessages: OrderingErrorMessagesState }) =>
    state.orderingErrorMessages.initialCustomizations[categoryId] ?? null

export const selectPendingAiSuggestion =
  (categoryId: string) =>
  (state: { orderingErrorMessages: OrderingErrorMessagesState }) =>
    state.orderingErrorMessages.pendingAiSuggestions[categoryId] ?? null

export const selectHasPendingAiSuggestions = (state: {
  orderingErrorMessages: OrderingErrorMessagesState
}) => Object.keys(state.orderingErrorMessages.pendingAiSuggestions).length > 0

export const selectPendingSuggestionsCount = (state: {
  orderingErrorMessages: OrderingErrorMessagesState
}) => Object.keys(state.orderingErrorMessages.pendingAiSuggestions).length

export const selectIsGeneratingCategory =
  (categoryId: string) =>
  (state: { orderingErrorMessages: OrderingErrorMessagesState }) =>
    state.orderingErrorMessages.generatingCategoryIds.includes(categoryId)

export const selectPendingSingleCategoryId = (state: {
  orderingErrorMessages: OrderingErrorMessagesState
}) => state.orderingErrorMessages.pendingSingleCategoryId
